import type { DatabaseColumnDefinition } from './types';

/** Columns required before a view type can render (user adds via setup prompt). */
export type ViewSetupRequirement = {
  kind: 'board' | 'calendar' | 'timeline';
  title: string;
  description: string;
  addButtonLabel: string;
  /** Column spec to create when the user clicks add (one click, explicit). */
  column: { name: string; type: string; config?: Record<string, unknown> };
};

export function selectColumns(columns: DatabaseColumnDefinition[]): DatabaseColumnDefinition[] {
  return columns.filter((c) => c.type === 'select');
}

export function dateColumns(columns: DatabaseColumnDefinition[]): DatabaseColumnDefinition[] {
  return columns.filter((c) => c.type === 'date');
}

export function boardViewNeedsSetup(columns: DatabaseColumnDefinition[]): boolean {
  return selectColumns(columns).length === 0;
}

export function boardViewNeedsColumnPick(
  columns: DatabaseColumnDefinition[],
  boardColumnId: string | undefined,
): boolean {
  const selects = selectColumns(columns);
  if (selects.length === 0) {
    return false;
  }
  if (!boardColumnId) {
    return true;
  }
  return !selects.some((c) => c.id === boardColumnId);
}

export function firstDateColumnId(columns: DatabaseColumnDefinition[]): string | undefined {
  return dateColumns(columns)[0]?.id;
}

export function calendarViewNeedsSetup(columns: DatabaseColumnDefinition[]): boolean {
  return dateColumns(columns).length === 0;
}

export function calendarViewNeedsColumnPick(
  columns: DatabaseColumnDefinition[],
  calendarColumnId: string | undefined,
): boolean {
  const dates = dateColumns(columns);
  if (dates.length === 0) {
    return false;
  }
  if (!calendarColumnId) {
    return true;
  }
  return !dates.some((c) => c.id === calendarColumnId);
}

export function timelineViewNeedsSetup(columns: DatabaseColumnDefinition[]): boolean {
  return dateColumns(columns).length === 0;
}

export function timelineViewNeedsColumnPick(
  columns: DatabaseColumnDefinition[],
  timelineColumnId: string | undefined,
): boolean {
  const dates = dateColumns(columns);
  if (dates.length === 0) {
    return false;
  }
  if (!timelineColumnId) {
    return true;
  }
  return !dates.some((c) => c.id === timelineColumnId);
}

export const BOARD_STATUS_COLUMN_SPEC = {
  name: 'Status',
  type: 'select',
  config: { options: ['To do', 'In progress', 'Done'] },
} as const;

export function boardSetupRequirement(): ViewSetupRequirement {
  return {
    kind: 'board',
    title: 'Select column required',
    description:
      'Board view groups rows into lanes using a Select column (for example Status). Add one to continue.',
    addButtonLabel: 'Add Status column',
    column: BOARD_STATUS_COLUMN_SPEC,
  };
}

/** Placeholder for a future calendar view (not implemented yet). */
export function calendarSetupRequirement(): ViewSetupRequirement {
  return {
    kind: 'calendar',
    title: 'Date column required',
    description:
      'Calendar view needs at least one Date column (single dates or from–to ranges). Add one to continue.',
    addButtonLabel: 'Add Date column',
    column: { name: 'Date', type: 'date', config: {} },
  };
}

/** Timeline view setup when no date column exists yet. */
export function timelineSetupRequirement(): ViewSetupRequirement {
  return {
    kind: 'timeline',
    title: 'Date column required',
    description:
      'Timeline view needs at least one Date column (single dates or from–to ranges). Add one to continue.',
    addButtonLabel: 'Add Date column',
    column: { name: 'Date', type: 'date', config: {} },
  };
}
